import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Dialog } from '../ui/Dialog/Dialog';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button';
import { Checkbox } from '../atoms/Checkbox';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography';
import './cameraSourceDialogs.css';
import { LiveRegion } from '../atoms/LiveRegion';

export interface TokenRevealDialogProps {
  /** Whether the dialog is currently open. */
  isOpen: boolean;
  /** The ingestion endpoint URL to display. */
  endpointUrl: string;
  /** The HTTP header name the device must send the token in. */
  headerName: string;
  /** The one-time device token value. */
  token: string;
  /** Dialog title; defaults to the camera source wording. */
  title?: string;
  /** Sentence under the title; defaults to the camera source wording. */
  description?: string;
  /**
   * Called only when the user has checked the acknowledgement checkbox and
   * explicitly clicked the Done button. This is the only way the dialog can
   * be dismissed.
   */
  onConfirm: () => void;
}

type CopyField = 'endpoint' | 'token';

const COPIED_FEEDBACK_MS = 2000;

export function TokenRevealDialog({
  isOpen,
  endpointUrl,
  headerName,
  token,
  title,
  description,
  onConfirm,
}: TokenRevealDialogProps) {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);
  const [copiedField, setCopiedField] = useState<CopyField | null>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  const handleCopy = async (field: CopyField, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard write can fail (permissions, insecure context); the values
      // stay visible so the user can still select and copy them manually.
      return;
    }
    setCopiedField(field);
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      setCopiedField(null);
      timeoutRef.current = null;
    }, COPIED_FEEDBACK_MS);
  };

  const handleDone = () => {
    if (!acknowledged) {
      return;
    }
    onConfirm();
  };

  const noop = () => {
    // Intentional no-op: the dialog is non-dismissible (no close button,
    // Escape or backdrop click); only the acknowledged Done button closes it.
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={noop}
      dismissible={false}
      title={title ?? t('cameraSources.reveal.title')}
      data-testid="token-reveal-dialog"
    >
      <Stack gap="4">
        <Text>{description ?? t('cameraSources.reveal.description')}</Text>

        <Stack gap="2">
          <Text variant="muted">{t('cameraSources.reveal.endpoint.label')}</Text>
          <Inline gap="2" align="center">
            <Text className="camera-source-dialog__value" data-testid="token-reveal-endpoint">
              {endpointUrl}
            </Text>
            <Button
              variant="secondary"
              onClick={() => {
                void handleCopy('endpoint', endpointUrl);
              }}
            >
              {t('cameraSources.reveal.copyEndpoint')}
            </Button>
          </Inline>
        </Stack>

        <Stack gap="2">
          <Text variant="muted">{t('cameraSources.reveal.header.label')}</Text>
          <Text className="camera-source-dialog__value" data-testid="token-reveal-header">
            {headerName}
          </Text>
        </Stack>

        <Stack gap="2">
          <Text variant="muted">{t('cameraSources.reveal.token.label')}</Text>
          <Inline gap="2" align="center">
            <Text className="camera-source-dialog__value" data-testid="token-reveal-token">
              {token}
            </Text>
            <Button
              variant="secondary"
              onClick={() => {
                void handleCopy('token', token);
              }}
            >
              {t('cameraSources.reveal.copyToken')}
            </Button>
          </Inline>
        </Stack>

        {/* Persistent live region so every copy success is announced. */}
        <LiveRegion as="span" aria-atomic="true">
          {copiedField !== null && (
            <Text as="span" variant="muted">
              {t('cameraSources.reveal.copied')}
            </Text>
          )}
        </LiveRegion>

        <Badge tone="warning">{t('cameraSources.reveal.warning')}</Badge>

        <Checkbox
          label={t('cameraSources.reveal.confirm')}
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
        />

        <Inline justify="end">
          <Button variant="primary" disabled={!acknowledged} onClick={handleDone}>
            {t('cameraSources.reveal.done')}
          </Button>
        </Inline>
      </Stack>
    </Dialog>
  );
}
